import React, { useMemo } from 'react';
import { Box, Button, ListItem, Typography, useTheme } from '@mui/material';
import { SportEvent } from '../../ses/types/SportEventTypes';
import { PlayerShift, StatKey } from '../types/PlayerShiftTypes';
import { formatGameClock, getEventPlayerAttributions } from '../utils/shiftUtils';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
import { EventActions } from '../../ses/components/EventActions';
import { GoalEventItem } from '../../ses/components/GoalEventItem';
import { useCurrentGame } from '../../games/context/CurrentGameContext';
import {
  timeTextStyle,
  descriptionTextStyle,
  teamLogoStyle,
  regularEventContainerStyle,
} from '../../ses/styles/playByPlayStyles';
import { getEventPayloadDescription } from '../../ses/utils/sportEventUtils';
import { getScore, getContrastTextColor } from '../../ses/components/PlayByPlayUtils';

interface ShiftCardProps {
  shift: PlayerShift;
  onDownloadShift: (shift: PlayerShift) => void;
  onClipCreate: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  onSeekToShift?: (shift: PlayerShift) => void;
  /**
   * If provided (non-empty), only events whose attribution matches one of
   * these stats AND belongs to `playerId` are shown inside the card.
   */
  activeFilters?: Set<StatKey>;
  /** Required when `activeFilters` is provided — used as the attribution match. */
  playerId?: string;
}

export const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onDownloadShift,
  onClipCreate,
  onSeekToEvent,
  onSeekToShift,
  activeFilters,
  playerId,
}) => {
  const theme = useTheme();
  const {
    homeTeamLogoPath,
    awayTeamLogoPath,
    homeTeamPrimaryColor,
    awayTeamPrimaryColor,
    gameTypeMetadata,
    game,
  } = useCurrentGame();

  const periodDisplay = decodePeriodDisplay(
    shift.period,
    gameTypeMetadata?.periodNamingMapping?.[0],
    game?.season?.seasonType,
  );
  const clockStart = formatGameClock(shift.gameClockStart);
  const clockEnd = formatGameClock(shift.gameClockEnd);

  // Mirror the shift-level filter at the event level: when filters are active,
  // only show events the player is attributed to for one of the active stats.
  // Otherwise fall through to the full event list.
  const displayedEvents = useMemo(() => {
    if (!activeFilters || activeFilters.size === 0 || !playerId) return shift.events;
    return shift.events.filter((event) =>
      getEventPlayerAttributions(event).some(
        (attr) => activeFilters.has(attr.stat) && attr.playerId === playerId,
      ),
    );
  }, [shift.events, activeFilters, playerId]);

  return (
    <Box
      sx={{
        display: 'flex',
        p: 1,
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        backgroundColor: theme.palette.cx.bg.surface3,
        borderRadius: 1,
        mb: 1,
      }}
    >
      {/* Shift header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          onClick={
            onSeekToShift
              ? (e) => {
                  e.stopPropagation();
                  onSeekToShift(shift);
                }
              : undefined
          }
          sx={{
            fontFamily: 'Sohne',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '20px',
            color: '#FFF',
            cursor: onSeekToShift ? 'pointer' : 'default',
            '&:hover': onSeekToShift
              ? { textDecoration: 'underline', textUnderlineOffset: '3px' }
              : undefined,
          }}
        >
          <Box
            component="span"
            sx={{ color: theme.palette.cx.text.secondary, width: 30 }}
          >
            {periodDisplay.abbreviated}
          </Box>{' '}
          {clockStart} - {clockEnd}
        </Typography>

        {shift.isCompleted && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDownloadShift(shift);
            }}
            sx={{
              display: 'flex',
              padding: '7px 16px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '100px',
              border: `1px solid ${theme.palette.cx.text.muted}`,
              background: theme.palette.cx.bg.elevated,
              textTransform: 'none',
            }}
          >
            Download Shift
          </Button>
        )}
      </Box>

      {/* Events inside the shift */}
      {displayedEvents.map((event, idx) => {
        const payload = event.payload as any;
        const isGoal = payload?.type === 'goal';

        if (isGoal) {
          return (
            <GoalEventRow
              key={event.id || idx}
              event={event}
              isFirst={idx === 0}
              isLast={idx === displayedEvents.length - 1}
              isShiftEvent={true}
              homeTeamLogoPath={homeTeamLogoPath}
              awayTeamLogoPath={awayTeamLogoPath}
              homeTeamPrimaryColor={homeTeamPrimaryColor}
              awayTeamPrimaryColor={awayTeamPrimaryColor}
              onClipCreate={onClipCreate}
              onSeekToEvent={onSeekToEvent}
            />
          );
        }

        return (
          <ShiftEventRow
            key={event.id || idx}
            event={event}
            isFirst={idx === 0}
            isLast={idx === displayedEvents.length - 1}
            isShiftEvent={true}
            eventIndex={idx}
            onClipCreate={onClipCreate}
            onSeekToEvent={onSeekToEvent}
          />
        );
      })}
    </Box>
  );
};

// ============================================================================
// Regular event row inside a shift
// ============================================================================

interface ShiftEventRowProps {
  event: SportEvent;
  eventIndex: number;
  onClipCreate: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  isFirst: boolean;
  isLast: boolean;
  isShiftEvent: boolean;
}

const ShiftEventRow: React.FC<ShiftEventRowProps> = ({
  event,
  eventIndex,
  onClipCreate,
  onSeekToEvent,
  isFirst,
  isLast,
  isShiftEvent,
}) => {
  const teamLogoPath = event._teamInfo?.logoPath || '';
  const gameTime = formatEventGameTime(event);
  const description = getEventPayloadDescription(event) || event.description || '';

  const bgColor = eventIndex % 2 === 0 ? '#27272E' : '#1F1F25';

  return (
    <ListItem
      sx={{
        ...regularEventContainerStyle(bgColor),
        ...(isFirst && { mt: 1 }),
        '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', cursor: 'pointer' },
        borderRadius: isShiftEvent
          ? isFirst && isLast
            ? '8px'
            : isFirst
              ? '8px 8px 0 0'
              : isLast
                ? '0 0 8px 8px'
                : 0
          : 0,
      }}
      onClick={() => onSeekToEvent(event)}
    >
      <Typography sx={{ ...timeTextStyle, color: 'rgba(255,255,255,0.7)' }}>{gameTime}</Typography>

      {teamLogoPath ? (
        <Box component="img" src={teamLogoPath} alt="team" sx={teamLogoStyle} />
      ) : (
        <Box sx={{ width: 32, height: 32 }} />
      )}

      <Typography
        sx={{ ...descriptionTextStyle, color: 'rgba(255,255,255,0.9)', fontSize: '13px', ml: 1 }}
      >
        {description}
      </Typography>

      <EventActions event={event} onClipCreate={onClipCreate} onDebug={() => {}} />
    </ListItem>
  );
};

// ============================================================================
// Goal event row (red highlighted)
// ============================================================================

interface GoalEventRowProps {
  event: SportEvent;
  homeTeamLogoPath: string;
  awayTeamLogoPath: string;
  homeTeamPrimaryColor: string;
  awayTeamPrimaryColor: string;
  onClipCreate: (event: SportEvent) => void;
  onSeekToEvent: (event: SportEvent) => void;
  isFirst: boolean;
  isLast: boolean;
  isShiftEvent: boolean;
}

const GoalEventRow: React.FC<GoalEventRowProps> = ({
  event,
  homeTeamLogoPath,
  awayTeamLogoPath,
  homeTeamPrimaryColor,
  awayTeamPrimaryColor,
  onClipCreate,
  onSeekToEvent,
  isFirst,
  isLast,
  isShiftEvent,
}) => {
  const borderRadius = isShiftEvent
    ? isFirst && isLast
      ? '8px'
      : isFirst
        ? '8px 8px 0 0'
        : isLast
          ? '0 0 8px 8px'
          : 0
    : 0;

  return (
    <Box sx={{ borderRadius, overflow: 'hidden', width: '100%', ...(isFirst && { mt: 1 }) }}>
      <GoalEventItem
        event={event}
        teamColor={event._teamInfo?.color || '#000'}
        textColor={getContrastTextColor(event._teamInfo?.color || '#000000')}
        teamLogoPath={event._teamInfo?.logoPath || ''}
        homeTeamLogoPath={homeTeamLogoPath}
        awayTeamLogoPath={awayTeamLogoPath}
        homeTeamPrimaryColor={homeTeamPrimaryColor}
        awayTeamPrimaryColor={awayTeamPrimaryColor}
        gameTime={formatEventGameTime(event)}
        description={getEventPayloadDescription(event) || event.description || ''}
        score={getScore(event)}
        onDebug={() => {}}
        onSeekToEvent={onSeekToEvent}
        onClipCreate={onClipCreate}
        isShiftEvent={isShiftEvent}
      />
    </Box>
  );
};

// ============================================================================
// Helpers
// ============================================================================

function formatEventGameTime(event: SportEvent): string {
  const clock = event.clock;
  if (!clock?.time) return '';
  const seconds = typeof clock.time === 'string' ? parseInt(clock.time, 10) : Number(clock.time);
  if (isNaN(seconds)) return '';
  return formatGameClock(seconds);
}
